/**
 * Per-subscription engine-only FLATTEN (operator surface, admin auth).
 *
 * Closes ONLY the positions THIS subscription opened — sized from our own trade
 * book, clamped to broker net (never over-closes into a reversing position),
 * leaving any MANUAL position untouched. DRY-RUN by default (plan only, places
 * nothing); `execute: true` places real MARKET closing orders. Those closes are
 * reduce-only, so they go through even a CLOSED kill-switch (panic-flatten).
 *
 * Backed by:
 *   POST /admin/subscriptions/{id}/flatten  { execute, reason, max_qty?, product?, ack_live? }
 *
 * `attempts: 1` — a deliberate, audited, money-moving action; never retried.
 */
import { z } from 'zod';

import type { ApiResult } from './client';
import { get as _get, post } from './client';

export const flattenPlanSchema = z.object({
  symbol: z.string(),
  side: z.string(), // BUY/SELL — opposite our holding
  qty: z.number(),
  engine_net: z.number(),
  broker_net: z.number(),
  manual_residual: z.number(),
});

export const flattenLegSchema = z.object({
  symbol: z.string(),
  side: z.string(),
  requested_qty: z.number(),
  filled_qty: z.number(),
  status: z.string(), // filled | partial | no_fill | error
  detail: z.string().nullable().optional(),
});

export const flattenResponseSchema = z.object({
  subscription_id: z.string(),
  dry_run: z.boolean(),
  engine_net: z.record(z.number()),
  broker_net: z.record(z.number()),
  plans: z.array(flattenPlanSchema),
  alarms: z.array(z.string()),
  errors: z.array(z.string()),
  legs: z.array(flattenLegSchema),
  placed: z.number(),
  failed: z.number(),
  flat_after: z.boolean(),
  note: z.string().nullable().optional(),
});
export type FlattenResult = z.infer<typeof flattenResponseSchema>;

export interface FlattenBody {
  execute: boolean;
  reason: string;
  max_qty?: number;
  product?: string;
  ack_live?: boolean;
}

export const flattenSubscription = (
  subscriptionId: string,
  body: FlattenBody,
  signal?: AbortSignal,
): Promise<ApiResult<FlattenResult>> =>
  post(
    `/admin/subscriptions/${encodeURIComponent(subscriptionId)}/flatten`,
    flattenResponseSchema,
    body,
    // 70s > the backend's 60s close window + 2s grace: the request must
    // outlive the closes, or an aborted UI invites a double-flatten while
    // real orders are still being placed.
    { signal, retry: { attempts: 1 }, timeoutMs: 70_000 },
  ) as Promise<ApiResult<FlattenResult>>;

// ─── System-wide flatten-all (the global EOD / panic exit) ──────────────────
export const flattenAllResponseSchema = z.object({
  dry_run: z.boolean(),
  subscription_count: z.number(),
  placed: z.number(),
  failed: z.number(),
  all_flat: z.boolean(),
  results: z.array(flattenResponseSchema),
});
export type FlattenAllResult = z.infer<typeof flattenAllResponseSchema>;

export const flattenAll = (
  body: { execute: boolean; reason: string; max_qty?: number; product?: string },
  signal?: AbortSignal,
): Promise<ApiResult<FlattenAllResult>> =>
  post('/admin/platform/flatten-all', flattenAllResponseSchema, body, {
    signal,
    retry: { attempts: 1 },
    timeoutMs: 190_000, // > backend 180s bound — never abort mid-closes
  }) as Promise<ApiResult<FlattenAllResult>>;

// ─── ACCOUNT-level emergency flatten (disarm-first; the proper panic path) ──
export const cancelSweepSchema = z.object({
  attempted: z.number(),
  cancelled: z.number(),
  failed: z.number(),
  skipped: z.number(),
  skipped_foreign: z.number().optional().default(0),
  errors: z.array(z.string()),
});

export const flattenAccountResponseSchema = z.object({
  broker_account_id: z.string(),
  dry_run: z.boolean(),
  disarmed_subscriptions: z.array(z.string()),
  cancel_sweep: cancelSweepSchema.nullable().optional(),
  placed: z.number(),
  failed: z.number(),
  all_flat: z.boolean(),
  retried: z.boolean(),
  escalated: z.boolean(),
  note: z.string(),
  results: z.array(flattenResponseSchema),
});
export type FlattenAccountResult = z.infer<typeof flattenAccountResponseSchema>;

export const flattenAccount = (
  accountId: string,
  body: FlattenBody,
  signal?: AbortSignal,
): Promise<ApiResult<FlattenAccountResult>> =>
  post(
    `/admin/broker-accounts/${encodeURIComponent(accountId)}/flatten`,
    flattenAccountResponseSchema,
    body,
    // > backend 180s bound (+disarms+2s grace) — never abort mid-closes.
    { signal, retry: { attempts: 1 }, timeoutMs: 190_000 },
  ) as Promise<ApiResult<FlattenAccountResult>>;

// ─── Reconcile-block state (the fail-closed gate's operator surface) ────────
export const reconcileBlockSchema = z.object({
  subscription_id: z.string(),
  live: z.boolean(),
  gated: z.boolean().optional().default(false),
  passed: z.boolean().optional().default(false),
  blocked: z.boolean().optional().default(false),
  detail: z.string().nullable().optional(),
});
export type ReconcileBlockState = z.infer<typeof reconcileBlockSchema>;

export const getReconcileBlock = (
  subscriptionId: string,
  signal?: AbortSignal,
): Promise<ApiResult<ReconcileBlockState>> =>
  // get() imported lazily below to avoid touching the existing import line.
  _get(
    `/admin/subscriptions/${encodeURIComponent(subscriptionId)}/reconcile-block`,
    reconcileBlockSchema,
    { signal },
  ) as Promise<ApiResult<ReconcileBlockState>>;

export const clearReconcileBlock = (
  subscriptionId: string,
  reason: string,
  signal?: AbortSignal,
): Promise<ApiResult<ReconcileBlockState>> =>
  post(
    `/admin/subscriptions/${encodeURIComponent(subscriptionId)}/clear-reconcile-block`,
    reconcileBlockSchema,
    { reason },
    { signal, retry: { attempts: 1 } },
  ) as Promise<ApiResult<ReconcileBlockState>>;
